import { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';
import { getScheduleLabel, eventChipClass, mergeCalendarEvents } from '../../utils/scheduleEvents';

function CalendarView({ viewMode, onViewChange, onDateClick, onEventClick }) {
  const { user } = useAuthStore();
  const [currentDate, setCurrentDate] = useState(new Date());

  const monthKey = format(currentDate, 'yyyy-MM');
  const rangeStart = format(startOfMonth(currentDate), 'yyyy-MM-dd');
  const rangeEnd = format(endOfMonth(currentDate), 'yyyy-MM-dd');

  const { data: workerSchedules, isLoading: loadingWorker } = useQuery({
    queryKey: ['schedules', monthKey, user?.role, user?.id],
    queryFn: async () => {
      const startDate = rangeStart;
      const endDate = rangeEnd;
      let response;
      if (user?.role === 'WORKER') {
        response = await api.get(`/workers/${user.id}/schedule?startDate=${startDate}&endDate=${endDate}`);
      } else {
        response = await api.get(`/workers/schedules?startDate=${startDate}&endDate=${endDate}`);
      }
      return response.data.data || [];
    },
  });

  const { data: mandateEvents = [], isLoading: loadingMandate } = useQuery({
    queryKey: ['mandate-schedule', monthKey],
    queryFn: async () => {
      const response = await api.get(
        `/mandates/schedule-events?startDate=${rangeStart}&endDate=${rangeEnd}`
      );
      return response.data.data || [];
    },
    enabled: user?.role !== 'WORKER',
  });

  const { data: jobEvents = [], isLoading: loadingJobs } = useQuery({
    queryKey: ['job-schedule-events', monthKey, user?.role, user?.id],
    queryFn: async () => {
      const response = await api.get(`/jobs/schedule-events?startDate=${rangeStart}&endDate=${rangeEnd}`);
      return response.data.data || [];
    },
  });

  const schedules = useMemo(() => {
    return mergeCalendarEvents(workerSchedules, jobEvents, user?.role === 'WORKER' ? [] : mandateEvents);
  }, [workerSchedules, jobEvents, mandateEvents, user?.role]);

  const isLoading = loadingWorker || loadingJobs || (user?.role !== 'WORKER' && loadingMandate);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart);
    const calendarEnd = endOfWeek(monthEnd);

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);

  const navigateDate = (direction) => {
    if (viewMode === 'month') {
      setCurrentDate(direction === 'next' ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1));
    } else {
      setCurrentDate(direction === 'next' ? addDays(currentDate, 1) : subDays(currentDate, 1));
    }
  };

  const parseScheduleDate = (dt) => {
    if (!dt) return null;
    const s = String(dt);
    const iso = s.includes(' ') ? s.replace(' ', 'T').slice(0, 19) : s;
    try {
      return parseISO(iso);
    } catch {
      return new Date(dt);
    }
  };

  const getEventsForDate = (date) => {
    if (!schedules) return [];
    return schedules.filter((schedule) => {
      const scheduleDate = parseScheduleDate(schedule.start_time);
      return scheduleDate && isSameDay(scheduleDate, date);
    });
  };

  const handleEventClick = (event, e) => {
    e?.stopPropagation();
    onEventClick(event);
  };

  const weekDays = useMemo(() => {
    if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate);
      return eachDayOfInterval({ start: weekStart, end: endOfWeek(currentDate) });
    }
    return [];
  }, [currentDate, viewMode]);

  if (isLoading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="bg-card rounded-lg shadow">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center space-x-4">
          <button onClick={() => navigateDate('prev')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-xl font-semibold">
            {viewMode === 'month' && format(currentDate, 'MMMM yyyy')}
            {viewMode === 'week' &&
              `${format(startOfWeek(currentDate), 'MMM d')} - ${format(endOfWeek(currentDate), 'MMM d, yyyy')}`}
            {viewMode === 'day' && format(currentDate, 'EEEE, MMMM d, yyyy')}
          </h2>
          <button onClick={() => navigateDate('next')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={() => setCurrentDate(new Date())}
            className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Today
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onViewChange('month')}
            className={`px-4 py-2 text-sm rounded-lg ${viewMode === 'month' ? 'bg-primary-600 text-white' : 'bg-gray-100'}`}
          >
            Month
          </button>
          <button
            onClick={() => onViewChange('week')}
            className={`px-4 py-2 text-sm rounded-lg ${viewMode === 'week' ? 'bg-primary-600 text-white' : 'bg-gray-100'}`}
          >
            Week
          </button>
          <button
            onClick={() => onViewChange('day')}
            className={`px-4 py-2 text-sm rounded-lg ${viewMode === 'day' ? 'bg-primary-600 text-white' : 'bg-gray-100'}`}
          >
            Day
          </button>
        </div>
      </div>

      <div className="px-4 py-2 border-b bg-gray-50 flex flex-wrap gap-3 text-xs text-gray-600">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-red-200" /> Inspection / due
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-blue-100" /> Worker schedule
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-violet-100" /> Mandate
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded bg-amber-100" /> Document request
        </span>
      </div>

      {viewMode === 'month' && (
        <div className="p-4">
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, idx) => {
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isToday = isSameDay(day, new Date());
              const dayEvents = getEventsForDate(day);

              return (
                <div
                  key={idx}
                  onClick={() => onDateClick(day)}
                  className={`
                    min-h-[100px] border rounded-lg p-2 cursor-pointer hover:bg-gray-50
                    ${!isCurrentMonth ? 'bg-gray-50 text-gray-400' : ''}
                    ${isToday ? 'border-primary-500 border-2' : 'border-gray-200'}
                  `}
                >
                  <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary-600' : ''}`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((event) => (
                      <div
                        key={event.id}
                        onClick={(e) => handleEventClick(event, e)}
                        className={`text-xs rounded px-1 py-0.5 truncate cursor-pointer hover:opacity-90 ${eventChipClass(event)}`}
                        title={getScheduleLabel(event)}
                      >
                        {getScheduleLabel(event)}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-gray-500">+{dayEvents.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewMode === 'week' && (
        <div className="p-4">
          <div className="grid grid-cols-8 gap-1">
            <div className="text-sm font-medium text-gray-600 py-2">Time</div>
            {weekDays.map((day) => (
              <div key={format(day, 'yyyy-MM-dd')} className="text-center text-sm font-medium text-gray-600 py-2">
                {format(day, 'EEE d')}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-8 gap-1">
            <div className="border-r" />
            {weekDays.map((day) => {
              const dayEvents = getEventsForDate(day);
              return (
                <div
                  key={format(day, 'yyyy-MM-dd')}
                  onClick={() => onDateClick(day)}
                  className={`min-h-[400px] border rounded-lg p-2 cursor-pointer hover:bg-gray-50 ${isSameDay(day, new Date()) ? 'border-primary-500 border-2' : ''}`}
                >
                  <div className="space-y-1">
                    {dayEvents.map((event) => (
                      <div
                        key={event.id}
                        onClick={(e) => handleEventClick(event, e)}
                        className={`text-xs rounded px-1 py-0.5 cursor-pointer hover:opacity-90 ${eventChipClass(event)}`}
                        title={getScheduleLabel(event)}
                      >
                        {getScheduleLabel(event)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewMode === 'day' && (
        <div className="p-4">
          <div className="space-y-2">
            {Array.from({ length: 24 }).map((_, hour) => {
              const hourDate = new Date(currentDate);
              hourDate.setHours(hour, 0, 0, 0);
              const hourEvents =
                schedules?.filter((schedule) => {
                  const scheduleDate = parseScheduleDate(schedule.start_time);
                  return scheduleDate && scheduleDate.getHours() === hour && isSameDay(scheduleDate, currentDate);
                }) || [];

              return (
                <div key={hour} className="flex border-b">
                  <div className="w-20 text-sm text-gray-600 py-2">{format(hourDate, 'h a')}</div>
                  <div className="flex-1 py-2">
                    {hourEvents.map((event) => (
                      <div
                        key={event.id}
                        onClick={() => onEventClick(event)}
                        className={`rounded px-2 py-1 mb-1 cursor-pointer hover:opacity-90 ${eventChipClass(event)}`}
                        title={getScheduleLabel(event)}
                      >
                        {getScheduleLabel(event)}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarView;
